import { useState, useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import { ChevronRight, Search, X } from "lucide-react";
import { NEWS_FULL } from "@/data/newsData";
import { NEWS_CATEGORIE } from "@/data/siteData";

const CAT_COLORS = {
  "Tutte":            { chip: "border-[var(--confcom-border)] text-[var(--confcom-text-secondary)] hover:border-[var(--confcom-blue)] hover:text-[var(--confcom-blue)]", active: "bg-[var(--confcom-blue)] text-white border-[var(--confcom-blue)]" },
  "Notizie":          { chip: "bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100",         active: "bg-blue-600 text-white border-blue-600" },
  "Bandi e credito":  { chip: "bg-green-50 text-green-700 border-green-200 hover:bg-green-100",     active: "bg-green-600 text-white border-green-600" },
  "Rappresentanza":   { chip: "bg-indigo-50 text-indigo-700 border-indigo-200 hover:bg-indigo-100", active: "bg-indigo-600 text-white border-indigo-600" },
  "Convenzioni":      { chip: "bg-teal-50 text-teal-700 border-teal-200 hover:bg-teal-100",         active: "bg-teal-600 text-white border-teal-600" },
  "Servizi":          { chip: "bg-orange-50 text-orange-700 border-orange-200 hover:bg-orange-100", active: "bg-orange-500 text-white border-orange-500" },
  "Formazione":       { chip: "bg-purple-50 text-purple-700 border-purple-200 hover:bg-purple-100", active: "bg-purple-600 text-white border-purple-600" },
  "Centro storico":   { chip: "bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100",     active: "bg-amber-500 text-white border-amber-500" },
  "Giubileo":         { chip: "bg-yellow-50 text-yellow-700 border-yellow-200 hover:bg-yellow-100", active: "bg-yellow-500 text-white border-yellow-500" },
  "AI | Innovazione": { chip: "bg-cyan-50 text-cyan-700 border-cyan-200 hover:bg-cyan-100",         active: "bg-cyan-600 text-white border-cyan-600" },
  "Lavoro e Welfare": { chip: "bg-pink-50 text-pink-700 border-pink-200 hover:bg-pink-100",         active: "bg-pink-600 text-white border-pink-600" },
};

export default function NotiziePage() {
  const [q, setQ] = useState("");
  const [activeCategory, setActiveCategory] = useState("Tutte");
  const filterRef = useRef(null);

  const scrollToResults = () => {
    if (filterRef.current) {
      window.scrollTo({ top: filterRef.current.offsetTop - (window.innerWidth >= 1024 ? 168 : 104), behavior: "smooth" });
    }
  };

  const filtered = useMemo(() => {
    let result = NEWS_FULL;
    if (activeCategory !== "Tutte") {
      result = result.filter((n) => n.categoria === activeCategory || (n.categorie || []).includes(activeCategory));
    }
    if (q.trim()) {
      const lq = q.toLowerCase();
      result = result.filter((n) =>
        n.titolo.toLowerCase().includes(lq) ||
        (n.excerpt || "").toLowerCase().includes(lq) ||
        (n.categoria || "").toLowerCase().includes(lq)
      );
    }
    return result;
  }, [q, activeCategory]);

  const isSearching = q.trim() !== "" || activeCategory !== "Tutte";

  return (
    <main id="main-content" data-testid="notizie-page">

      {/* Hero */}
      <section className="relative overflow-hidden" aria-labelledby="notizie-title">
        <img src="https://images.unsplash.com/photo-1586339949916-3e9457bef6d3?w=1920&q=60&auto=format" alt="Notizie" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0" style={{ backgroundColor: "rgba(4, 30, 66, 0.82)" }} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20 relative">
          <div className="max-w-3xl">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#56B7E6] font-['Encode_Sans_Semi_Condensed']">Informazione</span>
            <h1 id="notizie-title" className="text-4xl md:text-5xl font-bold text-white mt-3 font-['Encode_Sans_Semi_Condensed'] tracking-tight">Novità</h1>
          </div>
          <div className="mt-10 max-w-xl">
            <div className="relative">
              <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/50" aria-hidden="true" />
              <input
                type="text"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Cerca nelle notizie…"
                data-testid="notizie-search"
                className="w-full pl-11 pr-4 py-3.5 rounded-xl bg-white/15 border border-white/25 text-white placeholder-white/50 focus:outline-none focus:border-white/60 focus:bg-white/20 transition-all text-sm"
              />
              {q && (
                <button onClick={() => setQ("")} className="absolute right-4 top-1/2 -translate-y-1/2 text-white/60 hover:text-white transition-colors" aria-label="Cancella ricerca">
                  <X size={16} />
                </button>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Barra filtri sticky – full width */}
      <section ref={filterRef} className="sticky top-[104px] lg:top-[168px] z-20 bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-center gap-2 py-3">
            <span className="text-[10px] font-bold uppercase tracking-widest text-[var(--confcom-text-muted)] shrink-0">Tema</span>
            {NEWS_CATEGORIE.map((cat) => {
              const isActive = activeCategory === cat;
              const cc = CAT_COLORS[cat] || CAT_COLORS["Tutte"];
              const count = cat === "Tutte" ? NEWS_FULL.length : NEWS_FULL.filter((n) => n.categoria === cat || (n.categorie || []).includes(cat)).length;
              return (
                <button
                  key={cat}
                  onClick={() => { setActiveCategory(cat); scrollToResults(); }}
                  data-testid={`filter-${cat.toLowerCase().replace(/[\s|]/g, "-")}`}
                  aria-pressed={isActive}
                  className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1 rounded-full border transition-all ${isActive ? cc.active : cc.chip}`}
                >
                  {cat}
                  <span className={`text-[10px] font-bold px-1 py-0.5 rounded-full leading-none ${isActive ? "bg-white/25" : "bg-white/60"}`}>{count}</span>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Contenuto */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

          {/* Risultati / reset */}
          {isSearching && (
            <div className="flex items-center justify-between mb-6">
              <p className="text-sm text-[var(--confcom-text-secondary)]">
                <span className="font-bold text-[var(--confcom-text-primary)]">{filtered.length}</span> risultati trovati
              </p>
              <button
                onClick={() => { setQ(""); setActiveCategory("Tutte"); }}
                className="inline-flex items-center gap-1.5 text-xs font-semibold text-[var(--confcom-blue)] border border-[var(--confcom-blue)]/30 px-3 py-1.5 rounded-full hover:bg-[var(--confcom-bg-blue-light)] transition-colors"
                data-testid="reset-filtri"
              >
                <X size={12} /> Rimuovi filtri
              </button>
            </div>
          )}

          {/* Lista */}
          <div data-testid="news-list">
            {filtered.length === 0 && (
              <div className="text-center py-16">
                <p className="text-[var(--confcom-text-muted)] mb-3">Nessuna notizia trovata con i filtri selezionati.</p>
                <button onClick={() => { setQ(""); setActiveCategory("Tutte"); }} className="text-sm text-[var(--confcom-blue)] hover:underline">Reimposta i filtri</button>
              </div>
            )}

            {/* Articolo in evidenza */}
            {filtered.length > 0 && (
              <Link
                to={`/notizie/${filtered[0].slug}`}
                className="group grid md:grid-cols-2 gap-0 rounded-xl border border-[var(--confcom-border)] overflow-hidden hover:shadow-xl transition-all mb-8"
                data-testid={`news-featured-${filtered[0].id}`}
              >
                <div className="h-64 md:h-auto overflow-hidden">
                  <img src={filtered[0].image} alt={filtered[0].titolo} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
                <div className="p-8 flex flex-col justify-center">
                  <div className="flex items-center gap-3 mb-3">
                    <span className="text-xs font-medium text-[var(--confcom-blue)] bg-[var(--confcom-bg-blue-light)] px-2.5 py-1 rounded-md">{filtered[0].categoria}</span>
                    <time className="text-xs text-[var(--confcom-text-muted)]">{filtered[0].data}</time>
                  </div>
                  <h2 className="text-xl md:text-2xl font-bold text-[var(--confcom-text-primary)] font-['Encode_Sans_Semi_Condensed'] leading-snug group-hover:text-[var(--confcom-blue)] transition-colors mb-3">
                    {filtered[0].titolo}
                  </h2>
                  <p className="text-sm text-[var(--confcom-text-secondary)] leading-relaxed mb-4 line-clamp-3">{filtered[0].excerpt}</p>
                  <span className="inline-flex items-center gap-1 text-sm font-medium text-[var(--confcom-blue)]">Leggi l'articolo <ChevronRight size={16} /></span>
                </div>
              </Link>
            )}

            {/* Griglia restanti */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filtered.slice(1).map((n) => (
                <Link key={n.id} to={`/notizie/${n.slug}`} className="group bg-white rounded-xl border border-[var(--confcom-border)] overflow-hidden hover:shadow-lg transition-all" data-testid={`news-card-${n.id}`}>
                  <div className="h-48 overflow-hidden">
                    <img src={n.image} alt={n.titolo} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                  </div>
                  <div className="p-5">
                    <div className="flex items-center gap-3 mb-3">
                      <span className="text-xs font-medium text-[var(--confcom-blue)] bg-[var(--confcom-bg-blue-light)] px-2.5 py-1 rounded-md">{n.categoria}</span>
                      <time className="text-xs text-[var(--confcom-text-muted)]" dateTime={n.data}>{n.data}</time>
                    </div>
                    <h3 className="text-base font-semibold text-[var(--confcom-text-primary)] font-['Encode_Sans_Semi_Condensed'] leading-snug group-hover:text-[var(--confcom-blue)] transition-colors line-clamp-2 mb-2">{n.titolo}</h3>
                    <p className="text-sm text-[var(--confcom-text-secondary)] leading-relaxed line-clamp-2">{n.excerpt}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>

        </div>
      </section>
    </main>
  );
}
